import { FieldType, IFilter } from "@saleor/components/Filter";
import { AutocompleteFilterOpts, FilterOpts, MinMax } from "@saleor/types";
import {
  CourierFilter,
  DeliveryTypeEnum,
  StaffNameInput,
  StoreTypeEnum
} from "@saleor/types/globalTypes";
import {
  createAutocompleteField,
  createDateField,
  createMultipleTextField,
  createOptionsField,
  createTextField
} from "@saleor/utils/filters/fields";
import { defineMessages, IntlShape } from "react-intl";

export enum OrderFilterKeys {
  created = "created",
  customer = "customer",
  deliveryType = "deliveryType",
  status = "status",
  shipping = "shipping",
  verticalType = "verticalType",
  store = "store",
  paymentType = "paymentType",
  riderName = "riderName",
  riderFirstName = "riderFirstName",
  riderLastName = "riderLastName",
  orderCourier = "orderCourier",
  mall = "mall"
}

export enum PaymentType {
  CASH = "mirumee.payments.cash_on_delivery",
  CARD = "smop.payments.xendit",
  EWALLET_GRABPAY = "smop.payments.ewallet:GRABPAY",
  EWALLET_PAYMAYA = "smop.payments.ewallet:PAYMAYA",
  GCASH = "smop.payments.gcash",
  ONLINE_BANKING_BPI = "smop.payments.direct_debit:BPI",
  ONLINE_BANKING_CHINABANK = "smop.payments.direct_debit:CHINABANK",
  ONLINE_BANKING_RCBC = "smop.payments.direct_debit:RCBC",
  ONLINE_BANKING_UBP = "smop.payments.direct_debit:UBP"
}
export interface OrderListFilterOpts {
  created: FilterOpts<MinMax>;
  customer: FilterOpts<string>;
  deliveryType: FilterOpts<DeliveryTypeEnum[]>;
  shipping: FilterOpts<MinMax>;
  verticalType: FilterOpts<StoreTypeEnum[]>;
  store: FilterOpts<string>;
  paymentType: FilterOpts<PaymentType>;
  riderName: FilterOpts<StaffNameInput>;
  orderCourier: FilterOpts<string[]>;
  mall?: FilterOpts<string[]> & AutocompleteFilterOpts;
}

const messages = defineMessages({
  card: {
    defaultMessage: "Card",
    description: "payment type"
  },
  cash: {
    defaultMessage: "Cash",
    description: "payment type"
  },
  customer: {
    defaultMessage: "Customer Name",
    description: "order"
  },
  delivery: {
    defaultMessage: "Delivery Date",
    description: "delivery"
  },
  deliveryType: {
    defaultMessage: "Delivery Type",
    description: "delivery type"
  },
  eWalletGrabPay: {
    defaultMessage: "E-Wallet - GrabPay",
    description: "payment type"
  },
  eWalletMaya: {
    defaultMessage: "E-Wallet - Maya",
    description: "payment type"
  },
  express: {
    defaultMessage: "Express",
    description: "express"
  },
  expressLongDistance: {
    defaultMessage: "10km Express Delivery",
    description: "expressLongDistance"
  },
  foodAndBeverages: {
    defaultMessage: "Food and Beverage",
    description: "food and beverage"
  },
  gcash: {
    defaultMessage: "GCash",
    description: "payment type"
  },
  generalMerchandise: {
    defaultMessage: "General Merchandise",
    description: "general merchandise"
  },
  grab: {
    defaultMessage: "Grab",
    description: "grab"
  },
  grabExpress: {
    defaultMessage: "Grab Express",
    description: "grab express"
  },
  inHouse: {
    defaultMessage: "In House",
    description: "in house"
  },
  inStore: {
    defaultMessage: "In Store Pickup",
    description: "in store pickup"
  },
  mall: {
    defaultMessage: "Mall",
    description: "mall"
  },
  nationwide: {
    defaultMessage: "Nationwide Delivery",
    description: "nationwide delivery"
  },
  nextDay: {
    defaultMessage: "Standard Delivery",
    description: "standard delivery"
  },
  o2GO: {
    defaultMessage: "2GO",
    description: "2go"
  },
  onlineBankingBPI: {
    defaultMessage: "Online Banking - BPI",
    description: "payment type"
  },
  onlineBankingChinaBank: {
    defaultMessage: "Online Banking - Chinabank",
    description: "payment type"
  },
  onlineBankingRcbc: {
    defaultMessage: "Online Banking - RCBC",
    description: "payment type"
  },
  onlineBankingUbp: {
    defaultMessage: "Online Banking - UBP",
    description: "payment type"
  },
  orderCourier: {
    defaultMessage: "Courier",
    description: "courier"
  },
  pandagoAuto: {
    defaultMessage: "Pandago Auto",
    description: "Pandago Auto"
  },
  pandagoManual: {
    defaultMessage: "Pandago Manual",
    description: "Pandago Manual"
  },
  paymentType: {
    defaultMessage: "Payment Type",
    description: "payment type"
  },
  placed: {
    defaultMessage: "Order Date",
    description: "order"
  },
  sameDay: {
    defaultMessage: "Same Day Delivery",
    description: "same day delivery"
  },
  store: {
    defaultMessage: "Store",
    description: "store"
  },
  verticalType: {
    defaultMessage: "Vertical Type",
    description: "vertical type"
  }
});

export function createFilterStructure(
  intl: IntlShape,
  opts: OrderListFilterOpts,
  toExcludeOptions?: OrderFilterKeys[],
  specifiedDelTypeOptions?: DeliveryTypeEnum[]
): IFilter<OrderFilterKeys> {
  const deliveryTypeOptions = [
    {
      label: intl.formatMessage(messages.express),
      value: DeliveryTypeEnum.EXPRESS
    },
    {
      label: intl.formatMessage(messages.expressLongDistance),
      value: DeliveryTypeEnum.EXPRESS_LONG_DISTANCE
    },
    {
      label: intl.formatMessage(messages.nextDay),
      value: DeliveryTypeEnum.NEXT_DAY
    },
    {
      label: intl.formatMessage(messages.inStore),
      value: DeliveryTypeEnum.IN_STORE_PICKUP
    },
    {
      label: intl.formatMessage(messages.sameDay),
      value: DeliveryTypeEnum.SAME_DAY
    },
    {
      label: intl.formatMessage(messages.nationwide),
      value: DeliveryTypeEnum.NATIONWIDE_DELIVERY
    }
  ];

  let filteredDeliveryTypeOptions = deliveryTypeOptions;
  if (specifiedDelTypeOptions && specifiedDelTypeOptions.length > 0) {
    filteredDeliveryTypeOptions = deliveryTypeOptions.filter(option =>
      specifiedDelTypeOptions.includes(option.value)
    );
  }

  const filterStructure = [
    {
      ...createMultipleTextField(OrderFilterKeys.riderName, "Rider", [
        {
          active: opts.riderName.active,
          label: "First name",
          multiple: false,
          name: OrderFilterKeys.riderFirstName,
          type: FieldType.text,
          value: opts.riderName.value.firstName
        },
        {
          active: opts.riderName.active,
          label: "Last name",
          multiple: false,
          name: OrderFilterKeys.riderLastName,
          type: FieldType.text,
          value: opts.riderName.value.lastName
        }
      ]),
      active: opts.riderName.active
    },
    {
      ...createTextField(
        OrderFilterKeys.customer,
        intl.formatMessage(messages.customer),
        opts.customer.value
      ),
      active: opts.customer.active
    },
    {
      ...createDateField(
        OrderFilterKeys.created,
        intl.formatMessage(messages.placed),
        opts.created.value
      ),
      active: opts.created.active
    },
    {
      ...createDateField(
        OrderFilterKeys.shipping,
        intl.formatMessage(messages.delivery),
        opts.shipping.value
      ),
      active: opts.shipping.active
    },
    {
      ...createOptionsField(
        OrderFilterKeys.deliveryType,
        intl.formatMessage(messages.deliveryType),
        opts.deliveryType.value,
        true,
        filteredDeliveryTypeOptions
      ),
      active: opts.deliveryType.active
    },
    {
      ...createOptionsField(
        OrderFilterKeys.verticalType,
        intl.formatMessage(messages.verticalType),
        opts.verticalType.value,
        true,
        [
          {
            label: intl.formatMessage(messages.foodAndBeverages),
            value: StoreTypeEnum.FOOD_AND_BEVERAGES
          },
          {
            label: intl.formatMessage(messages.generalMerchandise),
            value: StoreTypeEnum.GENERAL_MERCHANDISE
          }
        ]
      ),
      active: opts.verticalType.active
    },
    {
      ...createTextField(
        OrderFilterKeys.store,
        intl.formatMessage(messages.store),
        opts.store.value
      ),
      active: opts.store.active
    },
    {
      ...createOptionsField(
        OrderFilterKeys.paymentType,
        intl.formatMessage(messages.paymentType),
        [opts.paymentType.value],
        false,
        [
          {
            label: intl.formatMessage(messages.cash),
            value: PaymentType.CASH
          },
          {
            label: intl.formatMessage(messages.card),
            value: PaymentType.CARD
          },
          {
            label: intl.formatMessage(messages.gcash),
            value: PaymentType.GCASH
          },
          {
            label: intl.formatMessage(messages.onlineBankingBPI),
            value: PaymentType.ONLINE_BANKING_BPI
          },
          {
            label: intl.formatMessage(messages.onlineBankingChinaBank),
            value: PaymentType.ONLINE_BANKING_CHINABANK
          },
          {
            label: intl.formatMessage(messages.onlineBankingRcbc),
            value: PaymentType.ONLINE_BANKING_RCBC
          },
          {
            label: intl.formatMessage(messages.onlineBankingUbp),
            value: PaymentType.ONLINE_BANKING_UBP
          },
          {
            label: intl.formatMessage(messages.eWalletGrabPay),
            value: PaymentType.EWALLET_GRABPAY
          },
          {
            label: intl.formatMessage(messages.eWalletMaya),
            value: PaymentType.EWALLET_PAYMAYA
          }
        ]
      ),
      active: opts.paymentType.active
    },
    {
      ...createOptionsField(
        OrderFilterKeys.orderCourier,
        intl.formatMessage(messages.orderCourier),
        opts.orderCourier.value,
        true,
        [
          {
            label: intl.formatMessage(messages.grab),
            value: CourierFilter.GRAB
          },
          {
            label: intl.formatMessage(messages.grabExpress),
            value: CourierFilter.GRAB_EXPRESS
          },
          {
            label: intl.formatMessage(messages.o2GO),
            value: CourierFilter.TWO_GO
          },
          {
            label: intl.formatMessage(messages.inHouse),
            value: CourierFilter.IN_HOUSE
          },
          {
            label: intl.formatMessage(messages.pandagoManual),
            value: CourierFilter.PANDAGO
          },
          {
            label: intl.formatMessage(messages.pandagoAuto),
            value: CourierFilter.PANDAGO_AUTO
          }
        ]
      ),
      active: opts.orderCourier.active
    },
    {
      ...createAutocompleteField(
        OrderFilterKeys.mall,
        intl.formatMessage(messages.mall),
        opts?.mall?.value,
        opts?.mall?.displayValues,
        true,
        opts?.mall?.choices,
        {
          hasMore: opts?.mall?.hasMore,
          initialSearch: "",
          loading: opts?.mall?.loading,
          onFetchMore: opts?.mall?.onFetchMore,
          onSearchChange: opts?.mall?.onSearchChange
        }
      ),
      active: opts?.mall?.active
    }
  ];

  return toExcludeOptions && toExcludeOptions.length > 0
    ? filterStructure.filter(
        filterItem => !toExcludeOptions.includes(filterItem.name)
      )
    : filterStructure;
}
